import { useCallback, useMemo } from 'react';
import { MdPersonAdd } from 'react-icons/md';
import { generatePath, useHistory } from 'react-router';
import {
  Button,
  FiltersContainer,
  getSortByString,
  Heading,
  ListboxMultiple,
  SearchInput,
  Table,
} from '@nbirl/nbi-common-ui-libs';

import { PATHS } from '../../shared/constants';
import { Role } from '../../shared/enum';
import { useQueryParams } from '../../shared/hooks';
import { BaseLayout } from '../../shared/layout';
import { TableQueryState } from '../../shared/models';
import { getUsersListColumns } from './constants';
import { useUsersQuery } from './queries';
import { getRoleLabel, getStatusLabel } from './utils';

type UserStatus = 'ENABLED' | 'DISABLED';

const statuses: UserStatus[] = ['ENABLED', 'DISABLED'];

type FiltersState = {
  status: UserStatus[];
  role: Role[];
  search: string;
};

export const Users = () => {
  const history = useHistory();

  const handleEdit = useCallback(
    (userId: string) => {
      history.push(generatePath(PATHS.EditUser, { userId }));
    },
    [history]
  );

  const columns = useMemo(() => getUsersListColumns(handleEdit), [handleEdit]);

  const [query, setQuery] = useQueryParams<TableQueryState & FiltersState>({
    pageSize: 50,
    pageIndex: 0,
    sortBy: getSortByString({ id: 'createdAt', desc: false }),
    status: [],
    role: [],
    search: '',
  });
  const { status, role, search, ...tableState } = query;

  const { isFetching, isLoading, isPreviousData, error, data } = useUsersQuery(
    query,
    {
      keepPreviousData: true,
    }
  );

  return (
    <BaseLayout className="space-y-6">
      <Heading>Manage Users</Heading>
      <div className="flex items-center space-x-6" data-testid="users-filters">
        <SearchInput
          className="w-80"
          value={search}
          onChange={(value) =>
            setQuery({ ...query, search: value, pageIndex: 0 })
          }
        />
        <FiltersContainer>
          <ListboxMultiple
            fullWidth={false}
            showSelectedItemsBadge={true}
            placeholder="Status"
            items={statuses}
            value={status}
            itemToString={(value) => getStatusLabel(value === 'ENABLED')}
            onChange={(value) =>
              setQuery({ ...query, status: value, pageIndex: 0 })
            }
          />
          <ListboxMultiple
            fullWidth={false}
            showSelectedItemsBadge={true}
            placeholder="Role"
            items={Object.values(Role)}
            value={role}
            itemToString={(value) => getRoleLabel(value)}
            onChange={(value) =>
              setQuery({ ...query, role: value, pageIndex: 0 })
            }
          />
        </FiltersContainer>
        <div className="flex-1" />
        <Button
          variant="outline"
          leftIcon={<MdPersonAdd />}
          onClick={() => history.push(PATHS.AddUser)}
        >
          Add user
        </Button>
      </div>
      <Table
        columns={columns}
        data={data?.content}
        hasRowDividers
        {...tableState}
        totalCount={data?.totalElements}
        pageCount={data?.totalPages}
        isLoading={isLoading}
        isFetching={isFetching}
        isPreviousData={isPreviousData}
        error={error}
        onStateChange={(state) =>
          setQuery({
            ...query,
            ...state,
          })
        }
      />
    </BaseLayout>
  );
};
